// ABOUTME: Campaign detail page showing overview metrics and tabbed interface
// ABOUTME: Displays campaign steps and enrollments with management capabilities

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { EmailCampaignResponse } from '../types/emailCampaign';
import CampaignStepsTab from '../components/admin/CampaignStepsTab';
import CampaignEnrollmentsTab from '../components/admin/CampaignEnrollmentsTab';
import ManualEnrollmentTab from '../components/admin/ManualEnrollmentTab';
import CreateCampaignForm from '../components/admin/CreateCampaignForm';
import SharedAppBar from '../components/SharedAppBar';
import { EXIT_CONDITION_LABELS } from '../utils/campaignUtils';
import { getCurrentAccessToken } from '../services/api';

export default function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState<EmailCampaignResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (id) {
      fetchCampaign();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchCampaign intentionally omitted; it is stable and adding it would cause extra fetches
  }, [id]);

  const fetchCampaign = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getCurrentAccessToken();
      const response = await axios.get<EmailCampaignResponse>(
        `${import.meta.env.VITE_API_URL}/api/admin/email-campaigns/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setCampaign(response.data);
    } catch (err: unknown) {
      console.error('Error fetching campaign:', err);
      const responseMessage = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message
        : undefined;
      setError(responseMessage || 'Failed to fetch campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/campaigns');
  };

  const handleEdit = () => {
    setEditModalOpen(true);
  };

  const handleEditSuccess = () => {
    setEditModalOpen(false);
    fetchCampaign(); // Refresh campaign data
  };

  const handleEditCancel = () => {
    setEditModalOpen(false);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!campaign) return;

    setDeleting(true);
    setError(null);

    try {
      const token = getCurrentAccessToken();
      const response = await axios.delete(
        `${import.meta.env.VITE_API_URL}/api/admin/email-campaigns/${campaign.id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      console.log('Campaign deleted:', response.data);

      // Navigate back to campaigns list
      navigate('/admin/campaigns');
    } catch (err: unknown) {
      console.error('Error deleting campaign:', err);
      const responseMessage = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message
        : undefined;
      setError(responseMessage || 'Failed to delete campaign');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <SharedAppBar title="Campaign Details" showBackButton onBackClick={handleBack} />
        <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error || !campaign) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <SharedAppBar title="Campaign Details" showBackButton onBackClick={handleBack} />
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error || 'Campaign not found'}</Alert>
          <Button onClick={handleBack} sx={{ mt: 2 }}>
            Back to Campaigns
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <SharedAppBar title={campaign.displayName} showBackButton onBackClick={handleBack} />

      <Box sx={{ p: 3 }}>
        {/* Overview Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h5" gutterBottom>
                  {campaign.displayName}
                </Typography>
                {campaign.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {campaign.description}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Chip
                    label={campaign.isActive ? 'Active' : 'Inactive'}
                    color={campaign.isActive ? 'success' : 'error'}
                    size="small"
                  />
                  <Chip
                    label={`Exit: ${EXIT_CONDITION_LABELS[campaign.exitConditionType]}`}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton onClick={handleEdit} title="Edit campaign">
                  <EditIcon />
                </IconButton>
                <IconButton onClick={handleDeleteClick} title="Delete campaign" color="error">
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 200px' }}>
                <Typography variant="h4" color="success.main">
                  {campaign.activeEnrollmentsCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Enrollments
                </Typography>
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Typography variant="h4" color="info.main">
                  {campaign.completedEnrollmentsCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed Enrollments
                </Typography>
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Typography variant="h4">
                  {campaign.totalEmailsSentCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Emails Sent
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card>
          <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
            <Tab label="Steps" />
            <Tab label="Enrollments" />
            <Tab label="Manual Enrollment" />
          </Tabs>

          <Box sx={{ p: 2 }}>
            {currentTab === 0 && <CampaignStepsTab steps={campaign.steps} />}
            {currentTab === 1 && (
              <CampaignEnrollmentsTab
                campaignId={campaign.id}
                campaignName={campaign.displayName}
              />
            )}
            {currentTab === 2 && (
              <ManualEnrollmentTab
                preSelectedCampaignId={campaign.id}
                onEnrollmentSuccess={fetchCampaign}
              />
            )}
          </Box>
        </Card>
      </Box>

      {/* Edit Campaign Modal */}
      <Dialog
        open={editModalOpen}
        onClose={handleEditCancel}
        maxWidth="md"
        fullWidth
      >
        {campaign && (
          <CreateCampaignForm
            onSuccess={handleEditSuccess}
            onCancel={handleEditCancel}
            editingCampaign={campaign}
          />
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Campaign</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{campaign?.displayName}</strong>?
            <br />
            <br />
            This will remove all enrollments and scheduled emails for this campaign.
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete Campaign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
